/**
 * Starter prompts for the composer's "Prompt library" picker, grouped by category.
 *
 * STATIC AND LABELLED AS SUCH — there is no backend endpoint for a prompt library (verified: no
 * `prompt.library` / `PromptLibrary` route or service anywhere in this codebase). This is the same
 * kind of starting point `SAMPLE_PROMPTS` already offers on an empty conversation, just organised
 * into categories the way a reader browsing a library expects, and with more of them. Selecting one
 * fills the composer — it never sends on its own — so nothing here is presented as saved, editable,
 * or backed by real persistence ("+ New Prompt", "Last modified" etc. do not exist here on purpose).
 */
export interface LibraryPrompt {
  title: string;
  prompt: string;
}

export interface PromptCategory {
  id: string;
  label: string;
  prompts: readonly LibraryPrompt[];
}

export const PROMPT_LIBRARY: readonly PromptCategory[] = [
  {
    id: "org-analysis",
    label: "Org Analysis",
    prompts: [
      {
        title: "Map this org's automation",
        prompt: "Give me a map of every automation (flow, trigger, process builder) that fires on Lead or Opportunity changes.",
      },
      {
        title: "Find the riskiest customizations",
        prompt: "Which customizations in this org would be riskiest to touch, and why?",
      },
      {
        title: "Summarize recent metadata changes",
        prompt: "Summarize what changed in this org's metadata over the last 30 days.",
      },
    ],
  },
  {
    id: "data-model",
    label: "Data Model & Risk",
    prompts: [
      {
        title: "Audit data model risk",
        prompt: "Audit risk in this org's data model — orphaned relationships, unenforced required fields, anything fragile.",
      },
      {
        title: "Explain a custom object's role",
        prompt: "Explain what StripeIntegrationSettings__mdt is used for and everywhere it's referenced.",
      },
      {
        title: "Find fields nothing reads",
        prompt: "Find custom fields that nothing in this org reads from — automation, Apex, or reports.",
      },
    ],
  },
  {
    id: "apex-quality",
    label: "Apex & Code Quality",
    prompts: [
      {
        title: "Find untested high-traffic Apex",
        prompt: "Find untested Apex classes with high call volume.",
      },
      {
        title: "Identify the most complex classes",
        prompt: "What are the most complex classes in this org, and what makes each one complex?",
      },
      {
        title: "Trace a bug to its source",
        prompt: "Trace where a NullPointerException on Opportunity save is most likely coming from.",
      },
    ],
  },
  {
    id: "automation",
    label: "Automation & Flows",
    prompts: [
      {
        title: "Find automation on every Lead",
        prompt: "Which automation runs on every Lead creation, in what order, and can any of it conflict?",
      },
      {
        title: "Consolidate duplicate flows",
        prompt: "Find flows that duplicate each other's logic and propose which to consolidate.",
      },
    ],
  },
];
